import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink, FlaskConical, RefreshCcw, Send } from "lucide-react";

const FIELDS = [
    {
        group: "SEO & Arama Motoru Doğrulama",
        items: [
            { key: "seo_google_site_verification", label: "Google Search Console doğrulama kodu (content değeri)", type: "text" },
            { key: "seo_bing_site_verification", label: "Bing Webmaster doğrulama kodu (msvalidate.01)", type: "text" },
            { key: "seo_yandex_verification", label: "Yandex doğrulama kodu", type: "text" },
        ],
    },
    {
        group: "Hero Alanı",
        items: [
            { key: "hero_h1_part1", label: "Başlık 1", type: "text" },
            { key: "hero_h1_part2", label: "Başlık 2 (gradient)", type: "text" },
            { key: "hero_h1_part3", label: "Başlık 3", type: "text" },
            { key: "hero_subtitle", label: "Alt başlık", type: "textarea" },
            { key: "hero_fee_badge", label: "Fee etiketi", type: "text" },
        ],
    },
    {
        group: "İstatistikler",
        items: [
            { key: "stats_ad_spend", label: "Yönetilen spend", type: "text" },
            { key: "stats_accounts", label: "Açılan hesap", type: "text" },
            { key: "stats_bans", label: "Operasyon sürekliliği", type: "text" },
            { key: "stats_provisioning", label: "Hesap açılış süresi", type: "text" },
        ],
    },
    {
        group: "Trustpilot",
        items: [
            { key: "trustpilot_rating", label: "Rating", type: "number", step: "0.1" },
            { key: "trustpilot_count", label: "Yorum sayısı", type: "number" },
        ],
    },
    {
        group: "Fiyatlandırma",
        items: [
            { key: "topup_fee_percent", label: "Top-up fee (%)", type: "number", step: "0.01" },
        ],
    },
    {
        group: "Ad Account — Global Min Top-up",
        items: [
            { key: "min_topup_meta_standard", label: "Meta Standard min top-up", type: "number" },
            { key: "min_topup_meta_flex", label: "Meta Flex min top-up", type: "number" },
            { key: "min_topup_google", label: "Google min top-up", type: "number" },
            { key: "min_topup_tiktok", label: "TikTok min top-up", type: "number" },
            { key: "min_topup_snapchat", label: "Snapchat min top-up", type: "number" },
            { key: "min_topup_bing", label: "Bing min top-up", type: "number" },
            { key: "min_topup_taboola", label: "Taboola min top-up", type: "number" },
            { key: "min_topup_outbrain", label: "Outbrain min top-up", type: "number" },
            { key: "min_existing_topup_usd", label: "Mevcut hesaba ek top-up minimumu ($)", type: "number" },
        ],
    },
    {
        group: "Contact",
        items: [
            { key: "contact_telegram", label: "Telegram URL", type: "text" },
            { key: "contact_whatsapp", label: "WhatsApp URL", type: "text" },
            { key: "contact_email", label: "Destek e-postası", type: "text" },
            { key: "contact_phone", label: "Destek telefonu", type: "text" },
            { key: "company_legal_name", label: "Yasal şirket adı", type: "text" },
            { key: "company_address", label: "Şirket adresi", type: "textarea" },
            { key: "service_scope", label: "Hizmet kapsamı", type: "textarea" },
            { key: "guarantee_note", label: "Garanti notu", type: "textarea" },
            { key: "pricing_transparency_note", label: "Fiyat şeffaflığı notu", type: "text" },
        ],
    },
    {
        group: "Meta — Account creation",
        items: [
            { key: "meta_page_invite_url", label: "Sayfa davet URL (Facebook)", type: "text" },
            { key: "meta_page_invite_handle", label: "Davet görünen adı", type: "text" },
            { key: "meta_page_invite_email", label: "Sayfa erişim e-postası (alternatif — boşsa gizlenir)", type: "text" },
        ],
    },
    {
        group: "Meta Pixel + Conversions API",
        items: [
            { key: "meta_pixel_id", label: "Meta Pixel ID (public)", type: "text" },
            { key: "meta_capi_token", label: "CAPI Token (sadece sunucu)", type: "text" },
            { key: "meta_capi_test_code", label: "Test event kodu (opsiyonel)", type: "text" },
        ],
    },
    {
        group: "Google Analytics + Google Ads",
        items: [
            { key: "google_site_verification", label: "Google Site Verification", type: "text" },
            { key: "ga4_measurement_id", label: "GA4 Measurement ID (G-...)", type: "text" },
            { key: "ga4_api_secret", label: "GA4 API Secret (server)", type: "text" },
            { key: "google_ads_conversion_id", label: "Google Ads Conversion ID (AW-...)", type: "text" },
            { key: "google_ads_qualified_lead_label", label: "Qualified Lead Label", type: "text" },
            { key: "google_ads_complete_registration_label", label: "Complete Registration Label", type: "text" },
            { key: "google_ads_begin_checkout_label", label: "Begin Checkout Label", type: "text" },
            { key: "google_ads_purchase_label", label: "Purchase Label", type: "text" },
            { key: "google_ads_application_approved_label", label: "Application Approved Label", type: "text" },
            { key: "google_ads_whatsapp_label", label: "WhatsApp Click Label", type: "text" },
            { key: "google_ads_telegram_label", label: "Telegram Click Label", type: "text" },
        ],
    },
    {
        group: "Telegram Bildirimleri (Kayıt & Para Yatırma)",
        items: [
            { key: "telegram_notifications_enabled", label: "Bildirimler aktif", type: "checkbox" },
            { key: "telegram_bot_token", label: "Bot Token (BotFather'dan)", type: "text" },
            { key: "telegram_admin_chat_id", label: "Grup / Chat ID (örn. -1001234567890)", type: "text" },
        ],
    },
];

const groupToTestId = (group) =>
    group
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

export default function AdminSiteSettings() {
    const { settings, refresh } = useSiteSettings();
    const [draft, setDraft] = useState(settings);
    const [baseSettings, setBaseSettings] = useState(settings);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [checklist, setChecklist] = useState(null);
    const [tgTesting, setTgTesting] = useState(false);

    const loadAdminSettings = async () => {
        try {
            const { data } = await api.get("/admin/site-settings");
            const merged = { ...settings, ...(data?.settings || {}) };
            setDraft(merged);
            setBaseSettings(merged);
            setDirty(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Ayarlar yüklenemedi.");
        }

        try {
            const { data } = await api.get("/admin/ads/conversion-checklist");
            setChecklist(data || null);
        } catch {
            setChecklist(null);
        }
    };

    const hasAdminTrackingFields =
        Boolean(draft?.ga4_measurement_id) ||
        Boolean(draft?.ga4_api_secret) ||
        Boolean(draft?.google_ads_conversion_id) ||
        Boolean(draft?.meta_capi_token) ||
        Boolean(draft?.google_site_verification);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            for (const k of Object.keys(draft)) {
                let v = draft[k];
                if (typeof baseSettings[k] === "number") v = parseFloat(v);
                payload[k] = v;
            }
            await api.patch("/admin/site-settings", { settings: payload });
            toast.success("Ayarlar kaydedildi. Siteye yansıdı.");
            await refresh();
            await loadAdminSettings();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setDraft(baseSettings);
        setDirty(false);
    };

    const triggerTestEvent = async (eventName) => {
        try {
            await api.post("/events/funnel", {
                event_name: eventName,
                email: draft?.contact_email || "support@adrise-agency.com",
                full_name: "Adrise Test Event",
                value: eventName === "purchase" ? 100 : 1,
                currency: "USD",
                content_name: `admin_test_${eventName}`,
                content_category: "admin_manual_test",
                page_path: "/admin/site-settings",
                page_title: "Admin Site Settings",
            });
            toast.success(`${eventName} test eventi gönderildi.`);
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    `${eventName} test eventi gönderilemedi.`,
            );
        }
    };

    const testTelegram = async () => {
        if (dirty) {
            toast.message("Önce değişiklikleri kaydedin, sonra test edin.");
            return;
        }
        setTgTesting(true);
        try {
            const { data } = await api.post("/admin/telegram/test", { note: "Admin panel testi" });
            toast.success(
                data?.enabled === false
                    ? "Test mesajı gönderildi (not: bildirimler kapalı — canlıda gönderilmez)."
                    : "Test mesajı Telegram grubuna gönderildi ✅",
            );
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Telegram testi başarısız.");
        } finally {
            setTgTesting(false);
        }
    };

    return (
        <div data-testid="admin-site-settings" className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">Site CMS</h1>
                    <p className="text-muted-foreground mt-1">
                        Yeniden deploy etmeden canlı metinleri güncelleyin.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to="/admin/ads-tracking" data-testid="open-ads-tracking-center-link">
                        <Button variant="outline" className="border-white/10 bg-white/[0.02] hover:bg-white/[0.06]">
                            Ads Tracking Center
                        </Button>
                    </Link>
                    <Button
                        variant="outline"
                        onClick={loadAdminSettings}
                        data-testid="settings-reload-admin"
                        className="border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                    >
                        <RefreshCcw className="w-4 h-4 mr-2" /> Admin verisini yenile
                    </Button>
                    {dirty && (
                        <Button
                            variant="outline"
                            onClick={reset}
                            data-testid="settings-reset"
                            className="border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                        >
                            <RotateCcw className="w-4 h-4 mr-2" /> Vazgeç
                        </Button>
                    )}
                    <Button
                        onClick={save}
                        disabled={!dirty || saving}
                        data-testid="settings-save"
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? "Kaydediliyor…" : "Değişiklikleri kaydet"}
                    </Button>
                </div>
            </div>

            {dirty && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-4 py-3 text-sm text-yellow-300">
                    Kaydedilmemiş değişiklikleriniz var.
                </div>
            )}

            {!hasAdminTrackingFields && (
                <div
                    className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 text-sm text-blue-200"
                    data-testid="settings-admin-reload-hint"
                >
                    Tracking alanları boş görünüyor. Üstteki “Admin verisini yenile” butonuna basarak
                    tam admin settings verisini çekin.
                </div>
            )}

            <div className="space-y-6">
                <div
                    data-testid="settings-group-ad-tracking-setup-guide"
                    className="bg-card border border-white/[0.07] rounded-2xl p-6"
                >
                    <h3 className="font-display font-semibold text-lg mb-2">
                        Reklam Takip Kurulum Rehberi (Nereden alınır?)
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Aşağıdaki değerleri ilgili platformdan alıp bu sayfadaki alanlara yapıştırın.
                    </p>

                    <div className="grid lg:grid-cols-2 gap-4">
                        <article
                            className="rounded-xl border border-white/10 bg-background/40 p-4"
                            data-testid="ga4-guide-card"
                        >
                            <h4 className="font-semibold text-sm">GA4 Measurement ID (G-...)</h4>
                            <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-4">
                                <li>analytics.google.com &gt; Admin</li>
                                <li>Data Streams &gt; Web stream</li>
                                <li>Measurement ID kopyalayın (G-XXXXXXX)</li>
                            </ol>
                            <a
                                href="https://analytics.google.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="ga4-guide-link"
                                className="inline-flex items-center gap-1.5 text-primary text-xs mt-3"
                            >
                                GA4 aç <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </article>

                        <article
                            className="rounded-xl border border-white/10 bg-background/40 p-4"
                            data-testid="ga4-api-secret-guide-card"
                        >
                            <h4 className="font-semibold text-sm">GA4 API Secret</h4>
                            <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-4">
                                <li>GA4 &gt; Admin &gt; Data Streams &gt; Web stream</li>
                                <li>Measurement Protocol API secrets</li>
                                <li>Create ile secret üretin, kopyalayın</li>
                            </ol>
                        </article>

                        <article
                            className="rounded-xl border border-white/10 bg-background/40 p-4"
                            data-testid="google-ads-guide-card"
                        >
                            <h4 className="font-semibold text-sm">Google Ads Conversion ID + Label</h4>
                            <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-4">
                                <li>ads.google.com &gt; Goals &gt; Conversions</li>
                                <li>Yeni conversion action açın veya mevcutu açın</li>
                                <li>
                                    Tag setup içinde <code>AW-XXXXXXX/label</code> görürsünüz
                                </li>
                                <li>
                                    <code>AW-...</code> kısmını Conversion ID,
                                    sonrasını ilgili Label alanına yazın
                                </li>
                            </ol>
                            <a
                                href="https://ads.google.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="google-ads-guide-link"
                                className="inline-flex items-center gap-1.5 text-primary text-xs mt-3"
                            >
                                Google Ads aç <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </article>

                        <article
                            className="rounded-xl border border-white/10 bg-background/40 p-4"
                            data-testid="gsc-guide-card"
                        >
                            <h4 className="font-semibold text-sm">Google Site Verification (Search Console)</h4>
                            <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-4">
                                <li>search.google.com/search-console açın</li>
                                <li>Property ekleyin veya mevcut property’yi seçin</li>
                                <li>HTML tag yöntemini seçin</li>
                                <li>
                                    <code>content=&quot;...&quot;</code> içindeki token’ı bu alana yapıştırın
                                </li>
                            </ol>
                            <a
                                href="https://search.google.com/search-console"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="gsc-guide-link"
                                className="inline-flex items-center gap-1.5 text-primary text-xs mt-3"
                            >
                                Search Console aç <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </article>

                        <article
                            className="rounded-xl border border-white/10 bg-background/40 p-4 lg:col-span-2"
                            data-testid="meta-capi-guide-card"
                        >
                            <h4 className="font-semibold text-sm">Meta Pixel ID + CAPI Token</h4>
                            <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-4">
                                <li>business.facebook.com &gt; Events Manager</li>
                                <li>Pixel seçin, Pixel ID kopyalayın</li>
                                <li>Conversions API &gt; Generate access token</li>
                                <li>Token’ı “CAPI Token” alanına girin</li>
                            </ol>
                            <a
                                href="https://business.facebook.com/events_manager2/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="meta-guide-link"
                                className="inline-flex items-center gap-1.5 text-primary text-xs mt-3"
                            >
                                Meta Events Manager aç <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </article>
                    </div>

                    <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="ga4-import-guide-box">
                        <h4 className="font-semibold text-sm mb-2">Google Ads’te GA4 event import nasıl açılır?</h4>
                        <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal pl-4">
                            <li>Google Ads &gt; Goals &gt; Conversions</li>
                            <li>New conversion action &gt; Import</li>
                            <li>Google Analytics 4 properties &amp; web</li>
                            <li>
                                Şu eventleri import edin: <code>qualified_lead</code>, <code>begin_checkout</code>, <code>purchase</code>, <code>application_approved</code>
                            </li>
                            <li>Her conversion için primary/secondary stratejisini kampanya tipine göre seçin</li>
                        </ol>
                    </div>

                    <div className="mt-4 rounded-xl border border-white/10 bg-background/40 p-4" data-testid="ads-readiness-status-box">
                        <h4 className="font-semibold text-sm mb-3">Anlık Kurulum Durumu</h4>
                        {!checklist ? (
                            <p className="text-xs text-muted-foreground">Durum bilgisi alınamadı.</p>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-2 text-xs">
                                {[
                                    ["GA4 Measurement ID", checklist.ga4?.measurement_id_set],
                                    ["GA4 API Secret", checklist.ga4?.api_secret_set],
                                    ["Google Ads Conversion ID", checklist.google_ads?.conversion_id_set],
                                    ["Qualified Lead Label", checklist.google_ads?.qualified_lead_label_set],
                                    ["Complete Registration Label", checklist.google_ads?.complete_registration_label_set],
                                    ["Begin Checkout Label", checklist.google_ads?.begin_checkout_label_set],
                                    ["Purchase Label", checklist.google_ads?.purchase_label_set],
                                    ["Application Approved Label", checklist.google_ads?.application_approved_label_set],
                                    ["WhatsApp Label", checklist.google_ads?.whatsapp_label_set],
                                    ["Telegram Label", checklist.google_ads?.telegram_label_set],
                                    ["Meta Pixel ID", checklist.meta?.pixel_id_set],
                                    ["Meta CAPI Token", checklist.meta?.capi_token_set],
                                    ["Search Console Verification", checklist.gsc?.site_verification_set],
                                ].map(([label, ok]) => (
                                    <div
                                        key={label}
                                        data-testid={`ads-readiness-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2"
                                    >
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className={ok ? "text-green-400" : "text-red-400"}>
                                            {ok ? "SET" : "MISSING"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div
                    data-testid="settings-group-event-test-tools"
                    className="bg-card border border-white/[0.07] rounded-2xl p-6"
                >
                    <h3 className="font-display font-semibold text-lg mb-2">
                        Event Test Araçları
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Aşağıdaki butonlar backend funnel endpointine test eventi gönderir. Events Manager / GA4 DebugView’den doğrulayabilirsiniz.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            "view_content",
                            "lead",
                            "qualified_lead",
                            "complete_registration",
                            "begin_checkout",
                            "purchase",
                            "application_approved",
                            "contact_whatsapp",
                            "contact_telegram",
                        ].map((eventName) => (
                            <Button
                                key={eventName}
                                type="button"
                                variant="outline"
                                onClick={() => triggerTestEvent(eventName)}
                                data-testid={`settings-test-event-${eventName}`}
                                className="border-white/15 bg-white/[0.02] hover:bg-white/[0.06]"
                            >
                                <FlaskConical className="w-4 h-4 mr-2" />
                                Test: {eventName}
                            </Button>
                        ))}
                    </div>
                </div>

                {FIELDS.map((g) => (
                    <div
                        key={g.group}
                        data-testid={`settings-group-${groupToTestId(g.group)}`}
                        className="bg-card border border-white/[0.07] rounded-2xl p-6"
                    >
                        <h3 className="font-display font-semibold text-lg mb-4">{g.group}</h3>
                        {g.group.startsWith("Telegram") && (
                            <div className="mb-5 rounded-xl border border-white/10 bg-background/40 p-4 text-sm text-muted-foreground space-y-2">
                                <p className="text-foreground font-medium">Kendi Adrise grubunuza bildirim almak için:</p>
                                <ol className="list-decimal pl-4 space-y-1 text-xs">
                                    <li>Telegram’da <span className="text-foreground">@BotFather</span>’a yazın → <span className="text-foreground">/newbot</span> ile bir bot oluşturun, size verdiği <span className="text-foreground">Bot Token</span>’ı aşağıya yapıştırın.</li>
                                    <li>Botu Adrise grubunuza ekleyin ve <span className="text-foreground">yönetici</span> yapın.</li>
                                    <li>Grup <span className="text-foreground">Chat ID</span>’sini alın (gruba <span className="text-foreground">@getidsbot</span> veya <span className="text-foreground">@RawDataBot</span> ekleyin; grup ID’si genelde <span className="text-foreground">-100…</span> ile başlar) ve aşağıya yazın.</li>
                                    <li><span className="text-foreground">Kaydet</span> deyin, sonra <span className="text-foreground">Test Gönder</span> ile grubu kontrol edin. Yeni kayıt ve para yatırma bildirimleri buraya düşer.</li>
                                </ol>
                                <div className="pt-2">
                                    <Button
                                        type="button"
                                        onClick={testTelegram}
                                        disabled={tgTesting}
                                        data-testid="telegram-test-button"
                                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                                    >
                                        <Send className="w-4 h-4 mr-2" />
                                        {tgTesting ? "Gönderiliyor…" : "Test mesajı gönder"}
                                    </Button>
                                    <span className="ml-3 text-xs">Önce kaydedin, sonra test edin.</span>
                                </div>
                            </div>
                        )}
                        <div className="grid md:grid-cols-2 gap-4">
                            {g.items.map((it) => (
                                <div key={it.key} className="space-y-1.5">
                                    <Label htmlFor={it.key}>{it.label}</Label>
                                    {it.type === "textarea" ? (
                                        <Textarea
                                            id={it.key}
                                            rows={4}
                                            value={draft[it.key] ?? ""}
                                            onChange={(e) => {
                                                setDraft((prev) => ({
                                                    ...(prev || {}),
                                                    [it.key]: e.target.value,
                                                }));
                                                setDirty(true);
                                            }}
                                            data-testid={`settings-${it.key}`}
                                            className="bg-background border-white/10"
                                        />
                                    ) : it.type === "checkbox" ? (
                                        <label className="inline-flex items-center gap-2 h-10">
                                            <input
                                                id={it.key}
                                                type="checkbox"
                                                checked={Boolean(draft[it.key])}
                                                onChange={(e) => {
                                                    setDraft((prev) => ({
                                                        ...(prev || {}),
                                                        [it.key]: e.target.checked,
                                                    }));
                                                    setDirty(true);
                                                }}
                                                data-testid={`settings-${it.key}`}
                                            />
                                            <span className="text-sm text-muted-foreground">Aktif</span>
                                        </label>
                                    ) : (
                                        <Input
                                            id={it.key}
                                            type={it.type}
                                            step={it.step}
                                            value={draft[it.key] ?? ""}
                                            onChange={(e) => {
                                                setDraft((prev) => ({
                                                    ...(prev || {}),
                                                    [it.key]: e.target.value,
                                                }));
                                                setDirty(true);
                                            }}
                                            data-testid={`settings-${it.key}`}
                                            className="bg-background border-white/10"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
